import { ClipboardIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useState, type FormEvent, type JSX } from 'react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { Label } from '@/components/ui/label.js';

import * as api from '../../services/integration-api.js';

import { useConnectorScope } from './connector-scope.js';

interface FormErrors {
  instanceUrl?: string;
  project?: string;
  apiKey?: string;
  submit?: string;
}

/**
 * The project-name shape identity accepts (letters, digits, `_` and `-`). The key becomes a segment
 * of every Report Portal API path, so the form refuses anything else before the round-trip does.
 */
const PROJECT_KEY = /^[A-Za-z0-9_-]+$/;

/**
 * Checks the three fields. The URL may carry a path, a query or a hash: people copy it from the
 * Report Portal UI (`/ui/#project/launches/all`), and only the origin is kept on submit.
 */
function validate(values: api.ReportPortalCredentialsPayload): FormErrors {
  const errors: FormErrors = {};
  try {
    const url = new URL(values.instanceUrl);
    if (!/^https?:$/.test(url.protocol)) {
      errors.instanceUrl = 'Use http:// or https://';
    }
  } catch {
    errors.instanceUrl =
      'Enter a valid URL (https://reportportal.your-org.com)';
  }
  if (!PROJECT_KEY.test(values.project)) {
    errors.project =
      'Enter the project key: letters, digits, "_" and "-", e.g. my_project';
  }
  if (values.apiKey.length < 8) {
    errors.apiKey = 'API key looks too short';
  }
  return errors;
}

/** The origin alone, which is what identity stores, so the user sees what lands on the connection. */
function toOrigin(value: string): string {
  return new URL(value).origin;
}

/**
 * Connect form for a self-hosted Report Portal: instance URL, project key and API key.
 *
 * The help text says where a key comes from and how quality runs get published without anyone
 * present: a dedicated Report Portal user's key, chosen in the Quality project settings, so the
 * launches do not belong to a person who may leave.
 */
export function ReportPortalCredentialsForm({
  onSaved,
  onCancel,
  details,
}: {
  onSaved: () => void;
  onCancel: () => void;
  /**
   * What the new connection should be called and when the agent should reach for it. Absent on a
   * first connection of the provider — there is nothing to tell it apart from.
   */
  details?: api.ConnectionCreateDetails;
}): JSX.Element {
  const [values, setValues] = useState<api.ReportPortalCredentialsPayload>({
    instanceUrl: '',
    project: '',
    apiKey: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  const { workspaceId } = useConnectorScope();

  const handleChange =
    (field: keyof api.ReportPortalCredentialsPayload) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const { value } = event.target;
      setValues((current) => ({ ...current, [field]: value }));
      if (errors[field]) {
        setErrors((current) => ({ ...current, [field]: undefined }));
      }
    };

  /** Reads the clipboard into the key field on an explicit user gesture. */
  const handlePasteFromClipboard = async (): Promise<void> => {
    setPasteError(null);
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      setValues((current) => ({ ...current, apiKey: text.trim() }));
      if (errors.apiKey) {
        setErrors((current) => ({ ...current, apiKey: undefined }));
      }
    } catch {
      setPasteError('Clipboard access denied. Paste manually with Cmd/Ctrl+V.');
    }
  };

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    const trimmed: api.ReportPortalCredentialsPayload = {
      instanceUrl: values.instanceUrl.trim(),
      project: values.project.trim(),
      apiKey: values.apiKey.trim(),
    };
    const found = validate(trimmed);
    if (Object.keys(found).length > 0) {
      setErrors(found);
      return;
    }
    setSubmitting(true);
    setErrors({});
    try {
      await api.saveReportPortalCredentials(
        { ...trimmed, instanceUrl: toOrigin(trimmed.instanceUrl) },
        workspaceId,
        details,
      );
      onSaved();
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to save credentials';
      setErrors({ submit: message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-3 flex flex-col gap-3"
      noValidate
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="reportportal-instance-url" className="text-xs">
          Instance URL
        </Label>
        <Input
          id="reportportal-instance-url"
          type="url"
          inputMode="url"
          autoComplete="url"
          spellCheck={false}
          placeholder="https://reportportal.your-org.com"
          value={values.instanceUrl}
          onChange={handleChange('instanceUrl')}
          disabled={submitting}
          aria-invalid={Boolean(errors.instanceUrl)}
          aria-describedby={
            errors.instanceUrl
              ? 'reportportal-instance-url-err'
              : 'reportportal-instance-url-help'
          }
        />
        {errors.instanceUrl ? (
          <p
            id="reportportal-instance-url-err"
            className="text-xs text-destructive"
          >
            {errors.instanceUrl}
          </p>
        ) : (
          <p
            id="reportportal-instance-url-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            The address you open Report Portal at. A link copied from inside it
            works too — only the address is kept.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="reportportal-project" className="text-xs">
          Project
        </Label>
        <Input
          id="reportportal-project"
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder="my_project"
          value={values.project}
          onChange={handleChange('project')}
          disabled={submitting}
          aria-invalid={Boolean(errors.project)}
          aria-describedby={
            errors.project
              ? 'reportportal-project-err'
              : 'reportportal-project-help'
          }
        />
        {errors.project ? (
          <p id="reportportal-project-err" className="text-xs text-destructive">
            {errors.project}
          </p>
        ) : (
          <p
            id="reportportal-project-help"
            className="text-[11px] leading-snug text-muted-foreground"
          >
            The project key from the Report Portal address bar — the part after{' '}
            <span className="font-medium">/ui/#</span>.
          </p>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="reportportal-api-key" className="text-xs">
          API key
        </Label>
        <div className="flex items-stretch gap-1.5">
          <div className="relative flex-1">
            <Input
              id="reportportal-api-key"
              type={showKey ? 'text' : 'password'}
              autoComplete="new-password"
              spellCheck={false}
              placeholder="Report Portal API key"
              value={values.apiKey}
              onChange={handleChange('apiKey')}
              disabled={submitting}
              aria-invalid={Boolean(errors.apiKey)}
              aria-describedby={
                errors.apiKey
                  ? 'reportportal-api-key-err'
                  : 'reportportal-api-key-help'
              }
              className="pr-8"
            />
            <button
              type="button"
              onClick={() => setShowKey((shown) => !shown)}
              aria-label={showKey ? 'Hide key' : 'Show key'}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex size-6 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-foreground/20"
            >
              {showKey ? (
                <EyeOffIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              ) : (
                <EyeIcon
                  className="size-3.5"
                  strokeWidth={1.75}
                  aria-hidden="true"
                />
              )}
            </button>
          </div>
          <button
            type="button"
            onClick={() => void handlePasteFromClipboard()}
            disabled={submitting}
            aria-label="Paste key from clipboard"
            className="inline-flex h-9 shrink-0 items-center justify-center gap-1 rounded-md border border-input bg-transparent px-2.5 text-xs font-medium text-foreground shadow-elev-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/20"
          >
            <ClipboardIcon
              className="size-3.5"
              strokeWidth={1.75}
              aria-hidden="true"
            />
            Paste
          </button>
        </div>
        {pasteError && (
          <p className="text-[11px] text-destructive">{pasteError}</p>
        )}
        {errors.apiKey ? (
          <p id="reportportal-api-key-err" className="text-xs text-destructive">
            {errors.apiKey}
          </p>
        ) : (
          <div
            id="reportportal-api-key-help"
            className="flex flex-col gap-1 text-[11px] leading-snug text-muted-foreground"
          >
            <p>
              Sign in to Report Portal, then open{' '}
              <span className="font-medium">Profile → API Keys</span> and
              generate a key. Paste it here.
            </p>
            <p>
              To publish quality runs to Report Portal, use the key of a
              dedicated Report Portal user rather than your own, then choose
              this connection in the Quality project settings.
            </p>
          </div>
        )}
      </div>
      {errors.submit && (
        <p className="text-xs text-destructive">{errors.submit}</p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={submitting}>
          {submitting ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    </form>
  );
}
